/**
 * DebugConsole — Captura remota de logs vía ring buffer on-demand.
 *
 * Flujo:
 *  1. Admin pulsa "Iniciar" → POST /admin/commands/debug {action:'start'}
 *     → Lambda publica DEBUG_START al MQTT topic device/{thing}/commands/down
 *     → Firmware aloca buffer en heap (lazy malloc) y empieza a capturar.
 *  2. Device responde por commands/up → response-correlator → WebSocket
 *     evento `command_response` con `commandType:'debug_ack'`.
 *  3. Mientras enabled, polea cada POLL_INTERVAL_MS con action:'fetch' & cursor.
 *  4. Cada chunk (debug_chunk) trae `data` (texto) + `endCursor` + `total` + `more`.
 *  5. Si la `generation` cambió (TTL expiró y se reabrió), reset de cursor.
 *  6. "Detener" libera el buffer en el ESP32 → cero costo de RAM.
 *
 * Diseño: el firmware tiene auto-disable por TTL para evitar sesiones huérfanas.
 */
import { useState, useEffect, useRef, useCallback } from 'react'
import { Bug, Play, StopCircle, Trash2, Download, RefreshCw, AlertTriangle } from 'lucide-react'
import { adminApi } from '../services/ApiService'
import WebSocketService from '../services/WebSocketService'
import './DebugConsole.css'

const POLL_INTERVAL_MS = 4000
const DEFAULT_SIZE = 4096
const DEFAULT_TTL_MIN = 5
const MAX_LOG_CHARS = 200_000 // ~200KB en el front

const SIZE_OPTIONS = [
  { value: 2048, label: '2 KB' },
  { value: 4096, label: '4 KB (recomendado)' },
  { value: 8192, label: '8 KB' },
]
const TTL_OPTIONS = [
  { value: 1, label: '1 min' },
  { value: 5, label: '5 min' },
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
]

export default function DebugConsole({ mac }) {
  const [enabled, setEnabled] = useState(false)
  const [starting, setStarting] = useState(false)
  const [error, setError] = useState(null)
  const [logs, setLogs] = useState('')
  const [stats, setStats] = useState(null)
  const [size, setSize] = useState(DEFAULT_SIZE)
  const [ttlMin, setTtlMin] = useState(DEFAULT_TTL_MIN)
  const [autoscroll, setAutoscroll] = useState(true)
  const [lastFetchAt, setLastFetchAt] = useState(null)

  const cursorRef = useRef(0)
  const generationRef = useRef(0)
  const pollTimerRef = useRef(null)
  const preRef = useRef(null)
  const enabledRef = useRef(false)
  const macUpper = (mac || '').toUpperCase()

  useEffect(() => { enabledRef.current = enabled }, [enabled])

  const stamp = () => `[${new Date().toLocaleTimeString('es-AR')}]`

  const appendLog = useCallback((text) => {
    if (!text) return
    setLogs(prev => {
      const next = prev + text
      return next.length > MAX_LOG_CHARS ? next.slice(-MAX_LOG_CHARS) : next
    })
  }, [])

  // ── WS handler: debug_ack / debug_chunk ──
  useEffect(() => {
    if (!macUpper) return

    const handler = (msg) => {
      if (msg?.event !== 'command_response') return
      if ((msg.mac || '').toUpperCase() !== macUpper) return
      const ct = msg.commandType
      if (ct !== 'debug_ack' && ct !== 'debug_chunk') return

      const d = msg.data || {}

      if (ct === 'debug_ack') {
        setStats({
          capacity: d.capacity || 0,
          used: d.used || 0,
          generation: d.generation || 0,
          ttlMs: d.ttlMs || 0,
          heap: d.heap || 0,
        })

        if (d.action === 'start') {
          if (d.success && d.enabled) {
            setEnabled(true)
            setError(null)
            cursorRef.current = 0
            generationRef.current = d.generation || 0
            appendLog(`\n${stamp()} >> Debug iniciado · buffer ${d.capacity}B · TTL ${Math.round((d.ttlMs || 0) / 1000)}s · heap libre ${d.heap}B\n`)
          } else {
            setEnabled(false)
            const reason = d.reason || (d.heap ? `heap insuficiente (${d.heap}B libre)` : 'rechazado por firmware')
            setError(reason)
            appendLog(`\n${stamp()} !! Debug rechazado: ${reason}\n`)
          }
        } else if (d.action === 'stop') {
          setEnabled(false)
          appendLog(`\n${stamp()} << Debug detenido\n`)
        }
      } else if (ct === 'debug_chunk') {
        setLastFetchAt(Date.now())

        if (!d.success) {
          if (d.enabled === false) {
            setEnabled(false)
            appendLog(`\n${stamp()} !! Sesión expirada en el dispositivo\n`)
          }
          return
        }

        // Generation cambió → sesión reabierta tras TTL, reset cursor
        if (generationRef.current !== 0 && d.generation !== generationRef.current) {
          appendLog(`\n${stamp()} !! Generation cambió (${generationRef.current}→${d.generation}), reset cursor\n`)
          cursorRef.current = 0
          generationRef.current = d.generation
          return
        }
        generationRef.current = d.generation || generationRef.current

        if (d.data) appendLog(d.data)
        if (typeof d.endCursor === 'number') cursorRef.current = d.endCursor

        setStats(prev => ({
          ...(prev || {}),
          generation: d.generation,
          used: d.total ?? prev?.used ?? 0,
          capacity: prev?.capacity || 0,
          ttlMs: prev?.ttlMs || 0,
        }))
      }
    }

    WebSocketService.on('*', handler)
    return () => WebSocketService.off('*', handler)
  }, [macUpper, appendLog])

  // ── Poll loop ──
  useEffect(() => {
    if (!enabled || !macUpper) return
    let cancelled = false

    const tick = async () => {
      if (cancelled || !enabledRef.current) return
      try {
        await adminApi.post('/admin/commands/debug', {
          mac: macUpper, action: 'fetch', cursor: cursorRef.current,
        })
      } catch (e) {
        console.warn('debug fetch failed:', e)
      }
    }

    tick()
    pollTimerRef.current = setInterval(tick, POLL_INTERVAL_MS)
    return () => {
      cancelled = true
      if (pollTimerRef.current) clearInterval(pollTimerRef.current)
    }
  }, [enabled, macUpper])

  // ── Autoscroll ──
  useEffect(() => {
    if (autoscroll && preRef.current) {
      preRef.current.scrollTop = preRef.current.scrollHeight
    }
  }, [logs, autoscroll])

  // ── Auto-stop al desmontar ──
  useEffect(() => {
    return () => {
      if (enabledRef.current && macUpper) {
        adminApi.post('/admin/commands/debug', { mac: macUpper, action: 'stop' }).catch(() => {})
      }
    }
  }, [macUpper])

  // ── Acciones ──
  const handleStart = async () => {
    if (!macUpper) return
    setError(null)
    setStarting(true)
    try {
      const ttlMs = Math.max(60_000, Math.min(1_800_000, ttlMin * 60_000))
      await adminApi.post('/admin/commands/debug', {
        mac: macUpper, action: 'start', size, ttlMs,
      })
      appendLog(`\n${stamp()} -> Solicitando inicio (${size} B, ${ttlMin} min)...\n`)
    } catch (e) {
      setError(e.message || 'Error al iniciar debug')
    } finally {
      setStarting(false)
    }
  }

  const handleStop = async () => {
    try {
      await adminApi.post('/admin/commands/debug', { mac: macUpper, action: 'stop' })
    } catch (e) {
      setError(e.message || 'Error al detener')
    }
  }

  const handleClear = () => setLogs('')

  const handleDownload = () => {
    if (!logs) return
    const blob = new Blob([logs], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `debug-${macUpper.replace(/:/g, '')}-${Date.now()}.log`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const usedPct = stats?.capacity ? Math.min(100, Math.round((stats.used / stats.capacity) * 100)) : 0

  // Coloriza una línea según su prefijo de severidad del firmware o marcadores JS
  const colorizeLine = (line, idx) => {
    // Formato firmware: [ms][E] / [ms][W] / [ms][I] / [ms][D]
    if (/\]\[E\]/.test(line)) return <span key={idx} className="dc-line dc-line-error">{line}</span>
    if (/\]\[W\]/.test(line)) return <span key={idx} className="dc-line dc-line-warn">{line}</span>
    if (/\]\[I\]/.test(line)) return <span key={idx} className="dc-line dc-line-info">{line}</span>
    if (/\]\[D\]/.test(line)) return <span key={idx} className="dc-line dc-line-debug">{line}</span>
    // Marcadores del lado JS (appendLog)
    if (/!! /.test(line)) return <span key={idx} className="dc-line dc-line-error">{line}</span>
    if (/>> /.test(line)) return <span key={idx} className="dc-line dc-line-system">{line}</span>
    if (/<< /.test(line)) return <span key={idx} className="dc-line dc-line-system">{line}</span>
    if (/--- DEBUG SESSION/.test(line)) return <span key={idx} className="dc-line dc-line-session">{line}</span>
    return <span key={idx} className="dc-line">{line}</span>
  }

  const renderLogs = () => {
    if (!logs) return null
    return logs.split('\n').map((line, i) => (
      <>{colorizeLine(line, i)}{'\n'}</>
    ))
  }

  return (
    <div className="info-section debug-console">
      <div className="dc-header">
        <h3 className="section-title">
          <Bug size={16} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '0.25rem' }} />
          Debug Remoto (Ring Buffer)
          <span className={`dc-status ${enabled ? 'dc-status-on' : 'dc-status-off'}`}>
            {enabled ? '● Activo' : '○ Inactivo'}
          </span>
        </h3>
      </div>

      {!enabled && (
        <div className="dc-controls">
          <div className="dc-control-group">
            <label>Buffer</label>
            <select value={size} onChange={e => setSize(parseInt(e.target.value))} disabled={starting}>
              {SIZE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="dc-control-group">
            <label>TTL (auto-stop)</label>
            <select value={ttlMin} onChange={e => setTtlMin(parseInt(e.target.value))} disabled={starting}>
              {TTL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <button className="dc-btn dc-btn-start" onClick={handleStart} disabled={starting || !macUpper}>
            <Play size={14} /> {starting ? 'Iniciando...' : 'Iniciar captura'}
          </button>
        </div>
      )}

      {enabled && (
        <div className="dc-controls">
          {stats && (
            <div className="dc-stats">
              <span>gen <b>{stats.generation}</b></span>
              <span>cursor <b>{cursorRef.current}</b></span>
              <span>
                buffer <b>{stats.used}/{stats.capacity}B</b>
                <span className="dc-bar"><span className="dc-bar-fill" style={{ width: `${usedPct}%` }} /></span>
              </span>
              {lastFetchAt && <span>último fetch <b>{Math.round((Date.now() - lastFetchAt) / 1000)}s</b></span>}
            </div>
          )}
          <button className="dc-btn dc-btn-stop" onClick={handleStop}>
            <StopCircle size={14} /> Detener
          </button>
        </div>
      )}

      <div className="dc-toolbar">
        <label className="dc-toggle">
          <input type="checkbox" checked={autoscroll} onChange={e => setAutoscroll(e.target.checked)} />
          Auto-scroll
        </label>
        <span className="dc-spacer" />
        <button className="dc-btn-ghost" onClick={handleClear} disabled={!logs}>
          <Trash2 size={12} /> Limpiar
        </button>
        <button className="dc-btn-ghost" onClick={handleDownload} disabled={!logs}>
          <Download size={12} /> Descargar .log
        </button>
      </div>

      {error && (
        <div className="dc-error">
          <AlertTriangle size={14} /> {error}
        </div>
      )}

      <pre ref={preRef} className="dc-terminal">
        {logs
          ? renderLogs()
          : (enabled
            ? 'Esperando datos del dispositivo...'
            : 'Pulsá "Iniciar captura" para activar el ring buffer en el ESP32 y empezar a recibir logs en tiempo real.')}
      </pre>

      <div className="dc-footer">
        <RefreshCw size={11} />
        El buffer vive solo en RAM del ESP32 mientras esté activo. Se libera automáticamente tras el TTL o al detener.
      </div>
    </div>
  )
}
